<template>
  <div class="tab-comment-item relative">
    <div class="flex">
      <OButton
        pure
        class="h-11"
        :href="userProfileUrl"
        v-bind="bind"
        @click="$analyticsEvent('User', 'User_Engagement', 'action_profile_click')"
      >
        <OAvatar :src="userAvatar" class="flex-shrink-0 rounded-full w-11 h-11 text-26"></OAvatar>
      </OButton>
      <div class="ml-4 comment-width">
        <header class="font-bold text-md flex items-center justify-between mb-1">
          <div class="w-full flex items-center">
            <OLink
              v-if="isUserHidden"
              :href="userProfileUrl"
              v-bind="bind"
              class="max-w-36 sm:max-w-72 whitespace-nowrap truncate"
              @click="$analyticsEvent('User', 'User_Engagement', 'action_profile_click')"
            >
              {{ item.user.name || item.user.username }}
            </OLink>
            <span v-else>{{ PASSIVE_USER }}</span>
            <OButton
              v-if="isMobile && item.user && !isMe && isUserHidden"
              pure
              size="xs"
              class="ml-2 hover:bg-gray-300 text-sm text-primary-200 rounded-sm"
              @click="handleSendMessage(item.user)"
            >
              <OIcon icon="envelope" svg-class="text-primary-300 w-[18px]" />
            </OButton>
            <OButton
              v-else-if="!isMe && isUserHidden"
              pure
              size="xs"
              class="ml-2 bg-gray-200 hover:bg-gray-300 py-0.5 px-2 font-medium text-sm text-primary-200 rounded-sm"
              @click="handleSendMessage(item.user)"
            >
              {{ $t('comment-item.send-message') }}
            </OButton>
          </div>
          <CommentsActionMenu v-if="!isMe" class="relative">
            <OButton
              :disabled="isReported"
              pure
              class="w-full flex items-center text-primary-100 text-sm font-medium hover:bg-alpha-5 px-4 py-2"
              :class="{ 'text-alpha-30': isReported }"
              @click="handleReport(item)"
            >
              <OIcon icon="megaphone" svg-class="w-6 h-6 sm:w-[18px] sm:h-[18px] mr-2.5" />
              {{ isReported ? $t('comment-item.complain-send') : $t('comment-item.complain') }}
            </OButton>
            <OButton
              pure
              class="w-full flex items-center text-primary-100 text-sm font-medium hover:bg-alpha-5 px-4 py-2"
              @click="handleBlock(item)"
            >
              <OIcon icon="block" svg-class="w-6 h-6 sm:w-[18px] sm:h-[18px] mr-2.5" />
              {{ isUserBlocked ? $t('comment-item.unblock') : $t('comment-item.block') }}
            </OButton>
          </CommentsActionMenu>
        </header>
        <p class="tab-comment-item-text text-sm leading-18 sm:text-md sm:leading-22 break-words">
          {{ commentText }}
          <span v-if="showAllTextButton" class="font-bold text-primary-300 text-sm hover:underline cursor-pointer" @click="showAllText">
            {{ $t('comment-item.see-more') }}
          </span>
        </p>
        <div class="text-sm font-medium flex items-center mt-2">
          <div class="flex items-center space-x-4">
            <OButton pure class="tab-comment-item-action u-center flex-shrink-0 text-primary-100" @click="handleReply(item)">
              <span class="">{{ $t('comment-item.reply') }}</span>
            </OButton>
            <OButton
              pure
              :disabled="isLikeDisabled"
              :class="[
                'u-center flex-shrink-0',
                `${isLikeDisabled ? 'cursor-not-allowed text-primary-300' : 'text-o-red'}`,
                `${isCommentLiked && 'font-bold'}`,
              ]"
              class="tab-comment-item-action"
              @click="toggleLikeComment(item)"
            >
              <OIcon :icon="isCommentLiked ? 'heart-comment-filled' : 'heart-comment'" />
              <span class="ml-1.5 text-primary-100">{{ $t('comment-item.like') }} ({{ item.ratings.likes }})</span>
            </OButton>
            <OButton v-if="isMe" pure class="tab-comment-item-action u-center flex-shrink-0 text-primary-100" @click="handleDelete(item)">
              <OIcon icon="trash" class="hidden sm:block w-3.5" />
              <span class="ml-1.5">{{ $t('main.dialog.delete') }}</span>
            </OButton>
          </div>
          <time class="text-primary-300 text-sm font-medium ml-auto">{{ commentDate }}</time>
        </div>
      </div>
    </div>
    <slot></slot>
  </div>
</template>

<script>
import { computed, onMounted, ref, useContext } from '@nuxtjs/composition-api';
import { truncate } from 'lodash';
import CommentsActionMenu from './CommentsActionMenu.vue';
import { OAvatar, OButton, OLink, OIcon } from '~/modules/common/components/ui';
import { getDateWithTime } from '~/modules/common/utils/dateUtils';
import { localStore } from '~/modules/common/utils/localStorageUtils';
import { getProfileUrl } from '~/modules/common/utils/routingUtils';
import { useDialog, useModal, useToast } from '~/composables/useModal';
import { useAuth } from '~/modules/auth/state/useAuth';
import { useDevice } from '~/composables/useDevice';
import { getImageUrl } from '~/modules/common/utils/imageUtils';
import { escapeText } from '~/modules/common/utils/stringUtils';
import { eventBus } from '~/modules/common/utils/eventBus';
import { PASSIVE_USER } from '~/constants/site';
import { MESSAGE_PATH } from '~/constants/config';

export default {
  components: {
    OAvatar,
    OButton,
    OLink,
    OIcon,
    CommentsActionMenu,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { isBot, isMobile } = useDevice();
    const { $service, $analyticsEvent, i18n } = useContext();
    const { openDialog, openErrorDialog } = useDialog();
    const { toastSuccess, toastDanger } = useToast();
    const { openLoginModal } = useModal();
    const { isLoggedIn, user } = useAuth();
    const userId = isLoggedIn.value ? user.value._id : 'anonym';
    const storageUserKey = `u${userId}`;
    const isCommentLiked = ref(false);
    const isMe = computed(() => isLoggedIn.value && props.item.user?._id === user.value._id);
    let addLikeStorage, removeLikeStorage, getCurrentUserLikes;

    const userLikes = ref({});

    if (!isBot.value) {
      getCurrentUserLikes = () => {
        const result = localStore.getItem('o-lks')[storageUserKey];
        return result;
      };

      addLikeStorage = (id) => {
        const likes = getCurrentUserLikes().concat(id);
        userLikes.value[storageUserKey] = likes;
        localStore.setItem('o-lks', userLikes.value);
      };

      removeLikeStorage = (id) => {
        const likes = getCurrentUserLikes().filter((likeId) => likeId !== id);
        userLikes.value[storageUserKey] = likes;
        localStore.setItem('o-lks', userLikes.value);
      };

      isCommentLiked.value = localStore.getItem('o-lks') ? localStore.getItem('o-lks')[storageUserKey]?.includes(props.item._id) : false;

      onMounted(() => {
        userLikes.value = localStore.getItem('o-lks');
        if (!userLikes.value) {
          userLikes.value = {};
          localStore.setItem('o-lks', userLikes.value);
        }
        if (!userLikes.value[storageUserKey]) {
          userLikes.value[storageUserKey] = [];
          localStore.setItem('o-lks', userLikes.value);
        }
      });
    }

    const isLikeDisabled = ref(false);

    const userAvatar = computed(() => {
      const img = getImageUrl(props.item.user?.avatar, { width: 100 });
      if (!img) return '';
      return img;
    });

    const userProfileUrl = getProfileUrl({ user: props.item.user });
    const isUserHidden = computed(() => !!props.item.user?.username); // user içinde username yok ise null dönüyor

    const toggleLikeComment = async (comment) => {
      isLikeDisabled.value = true;
      try {
        if (getCurrentUserLikes().includes(comment._id)) {
          await $service.article.unLikeComment(comment._id);
          isCommentLiked.value = false;
          comment.ratings.likes -= 1;
          removeLikeStorage(comment._id);
        } else {
          await $service.article.likeComment(comment._id);
          if (comment.parent) {
            $analyticsEvent('User', 'User_Engagement', 'action_reply_like');
          } else {
            $analyticsEvent('User', 'User_Engagement', 'action_comment_like');
          }
          comment.ratings.likes += 1;
          isCommentLiked.value = true;
          addLikeStorage(comment._id);
        }
      } catch (error) {
        openErrorDialog({ error });
      }
      isLikeDisabled.value = false;
    };

    const handleReply = (comment) => {
      if (!isLoggedIn.value) {
        openLoginModal();
        return;
      }
      emit('reply', comment);
    };

    const isReported = ref(false);
    const handleReport = (comment) => {
      eventBus.emit('close-comment-menu', comment._id);
      if (isReported.value) return;
      if (!isLoggedIn.value) {
        openLoginModal();
        return;
      }
      openDialog({
        description: i18n.t('comment-item.complain-dialog'),
        actions: [
          { text: i18n.t('main.dialog.cancel') },
          {
            text: i18n.t('comment-item.complain'),
            type: 'danger',
            action: async (closeDialog) => {
              try {
                await $service.article.reportComment(comment._id);
                toastSuccess(i18n.t('comment-item.complain-send'));
                isReported.value = true;
                $analyticsEvent('User', 'User_Engagement', 'action_report');
              } catch (error) {
                toastDanger(i18n.t('comment-item.failed-operation'));
              } finally {
                closeDialog();
              }
            },
          },
        ],
      });
    };

    const handleDelete = (comment) => {
      openDialog({
        description: i18n.t('comment-item.delete-comment'),
        actions: [
          { text: i18n.t('main.dialog.cancel') },
          {
            text: i18n.t('main.dialog.delete'),
            type: 'danger',
            action: async (closeDialog) => {
              try {
                await $service.article.deleteComment(comment._id);
                emit('delete', comment);
                toastSuccess(i18n.t('comment-item.deleted-comment'));
              } catch (error) {
                toastDanger(i18n.t('comment-item.failed-operation'));
              } finally {
                closeDialog();
              }
            },
          },
        ],
      });
    };

    const isUserBlocked = computed(() => props.item.user?.blocked);
    const handleBlock = (comment) => {
      eventBus.emit('close-comment-menu');
      if (!isLoggedIn.value) {
        openLoginModal();
        return;
      }
      openDialog({
        description: isUserBlocked.value ? i18n.t('comment-item.unblock-user') : i18n.t('comment-item.block-user'),
        actions: [
          { text: i18n.t('main.dialog.cancel') },
          {
            text: isUserBlocked.value ? i18n.t('comment-item.unblock') : i18n.t('comment-item.block'),
            type: 'danger',
            action: async (closeDialog) => {
              try {
                if (isUserBlocked.value) {
                  await $service.user.unBlockUser(comment.user._id);
                  toastSuccess(i18n.t('comment-item.unblocked-message'));
                  props.item.user.blocked = false;
                  $analyticsEvent('User', 'User_Engagement', 'action_unblocked');
                } else {
                  await $service.user.blockUser(comment.user._id);
                  toastSuccess(i18n.t('comment-item.blocked-message'));
                  props.item.user.blocked = true;
                  $analyticsEvent('User', 'User_Engagement', 'action_blocked');
                }
              } catch (error) {
                toastDanger(i18n.t('comment-item.failed-operation'));
              } finally {
                closeDialog();
              }
            },
          },
        ],
      });
    };

    const handleSendMessage = (user) => {
      if (!isLoggedIn.value) {
        openLoginModal();
        return;
      }
      $analyticsEvent('User', 'User_Engagement', 'action_message');
      if (isMobile.value) {
        window.location.href = `/${MESSAGE_PATH}/${user.username}/${user._id}`;
      } else {
        eventBus.emit('open-conversation', { user });
      }
    };

    const commentDate = computed(() => getDateWithTime(props.item.dates?.created));

    // comment truncate case
    const commentText = ref(truncate(escapeText(props.item.text), { length: 160 }));
    const showAllTextButton = ref(props.item.text?.length > 160);
    const showAllText = () => {
      showAllTextButton.value = false;
      commentText.value = escapeText(props.item.text);
    };

    const bind = {};
    bind.rel = 'nofollow';

    return {
      userAvatar,
      isUserHidden,
      isLikeDisabled,
      isUserBlocked,
      isReported,
      userProfileUrl,
      PASSIVE_USER,
      commentDate,
      toggleLikeComment,
      handleReply,
      handleReport,
      handleDelete,
      handleBlock,
      handleSendMessage,
      isMobile,
      isMe,
      isLoggedIn,
      isCommentLiked,
      commentText,
      showAllText,
      showAllTextButton,
      bind,
    };
  },
};
</script>

<style lang="scss">
.comment-width {
  width: calc(100% - 55px);
}
</style>
